import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "../hooks/useEntries";
import { t } from "../i18n";
import { getThemePalette } from "../config/themePalette";
import { Target, Check, X, Pencil, Palette, Plus, AlertTriangle } from "lucide-react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", GBP: "£" };

export default function Presupuestos() {
  const { profile, updateConfig, lang, currency, theme } = useAuth();
  const { visibleEntries } = useEntries();
  const c = profile?.config || {};
  const T = (key) => t(lang, key);
  const sym = CURRENCY_SYMBOLS[currency] || "€";
  const fmt = n => sym + Number(n).toLocaleString("es-ES",{minimumFractionDigits:2,maximumFractionDigits:2});
  const budgetLabel = c.budget_label || (lang==="en"?"Budgets":"Presupuestos");

  // Paleta de colores que el usuario puede elegir para cada presupuesto:
  // "theme" usa el color del tema activo (comportamiento por defecto), y
  // el resto son los colores cálidos/pastel filtrados según el tema
  // (dorados en King, rosas en Sakura, azules en Zafiro...), en vez de una
  // paleta fija de colores tipo Tailwind sin relación con el tema.
  const COLOR_PALETTE = useMemo(() => {
    const palette = getThemePalette(theme);
    return [
      { id: "theme", color: null },
      ...palette.map((hex, i) => ({ id: `c${i}`, color: hex })),
    ];
  }, [theme]);

  const [budgets, setBudgets] = useState(c.budgets || {});
  const [budgetColors, setBudgetColors] = useState(c.budget_colors || {});
  useEffect(() => {
    setBudgets(c.budgets || {});
    setBudgetColors(c.budget_colors || {});
  }, [profile]);

  const [newCat, setNewCat] = useState("");
  const [newAmt, setNewAmt] = useState("");
  const [saved, setSaved] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [editNameVal, setEditNameVal] = useState("");

  const now = new Date();
  const currentMonth = MONTHS[now.getMonth()];
  const currentYear = now.getFullYear();

  const expByCategory = useMemo(() => {
    const result = {};
    visibleEntries
      .filter(e => e.type === "expense" && e.month === currentMonth && e.year === currentYear)
      .forEach(e => {
        const cat = e.cat || "General";
        result[cat] = (result[cat] || 0) + e.amount;
      });
    return result;
  }, [visibleEntries, currentMonth, currentYear]);

  const budgetItems = useMemo(() =>
    Object.entries(budgets).map(([cat, limit]) => ({
      cat,
      limit: parseFloat(limit),
      spent: expByCategory[cat] || 0,
      pct: Math.min(100, Math.round(((expByCategory[cat] || 0) / parseFloat(limit)) * 100)),
      remaining: parseFloat(limit) - (expByCategory[cat] || 0),
    })).filter(b => b.limit > 0)
  , [budgets, expByCategory]);

  const expenseCats = (c.expense_cats || []);

  // Guardar automáticamente en Firestore
  async function saveBudgets(newBudgets, newColors) {
    setBudgets(newBudgets);
    if (newColors !== undefined) setBudgetColors(newColors);
    await updateConfig({ ...c, budgets: newBudgets, budget_colors: newColors !== undefined ? newColors : budgetColors });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  async function addBudget() {
    if (!newCat || !newAmt) return;
    const updated = { ...budgets, [newCat]: parseFloat(newAmt) };
    setNewCat(""); setNewAmt("");
    await saveBudgets(updated, budgetColors);
  }

  async function removeBudget(cat) {
    const updated = { ...budgets };
    delete updated[cat];
    const updatedColors = { ...budgetColors };
    delete updatedColors[cat];
    await saveBudgets(updated, updatedColors);
  }

  async function updateColor(cat, colorId) {
    const updatedColors = { ...budgetColors, [cat]: colorId };
    await saveBudgets(budgets, updatedColors);
  }

  async function renameBudget(oldCat, newName) {
    if (!newName.trim() || newName.trim() === oldCat) { setEditingName(null); return; }
    const trimmed = newName.trim();
    const updatedBudgets = { ...budgets, [trimmed]: budgets[oldCat] };
    delete updatedBudgets[oldCat];
    const updatedColors = { ...budgetColors, [trimmed]: budgetColors[oldCat] };
    delete updatedColors[oldCat];
    setEditingName(null);
    await saveBudgets(updatedBudgets, updatedColors);
  }

  function getBarColor(cat) {
    const colorId = budgetColors[cat] || "theme";
    const found = COLOR_PALETTE.find(cp => cp.id === colorId);
    return found?.color || "var(--blue)";
  }

  const totalBudget = budgetItems.reduce((s, b) => s + b.limit, 0);
  const totalSpent  = budgetItems.reduce((s, b) => s + b.spent, 0);
  const totalPct    = totalBudget > 0 ? Math.min(100, Math.round((totalSpent / totalBudget) * 100)) : 0;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><Target size={22}/> {budgetLabel}</h2>
          <p className="page-sub">
            {lang === "en" ? "Monthly spending limits by category" : "Límites de gasto mensual por categoría"} · {currentMonth} {currentYear}
          </p>
        </div>
        {saved && (
          <span style={{fontSize:13,color:"var(--green)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}><Check size={14}/> {lang==="en"?"Saved":"Guardado"}</span>
        )}
      </div>

      {/* Resumen global */}
      {budgetItems.length > 0 && (
        <div className="card" style={{marginBottom:24,padding:"20px 24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <span style={{fontSize:14,fontWeight:600,color:"var(--gray-700)"}}>
              {lang === "en" ? "Total this month" : "Total este mes"}
            </span>
            <span style={{fontSize:14,fontWeight:700,color:totalPct>=100?"var(--red)":totalPct>=80?"var(--amber)":"var(--green)"}}>
              {fmt(totalSpent)} / {fmt(totalBudget)} ({totalPct}%)
            </span>
          </div>
          <div className="limit-bar" style={{height:10}}>
            <div className={`limit-fill ${totalPct>=100?"full":totalPct>=80?"warn":""}`}
                 style={{width:`${totalPct}%`}} />
          </div>
        </div>
      )}

      {/* Barras por categoría */}
      {budgetItems.length > 0 && (
        <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:24}}>
          {budgetItems.map(b => (
            <div key={b.cat} className="card">
              {/* Cabecera: nombre editable + % + eliminar */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div style={{flex:1,minWidth:0}}>
                  {editingName === b.cat ? (
                    <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                      <input
                        autoFocus
                        value={editNameVal}
                        onChange={e=>setEditNameVal(e.target.value)}
                        onKeyDown={e=>{ if(e.key==="Enter") renameBudget(b.cat,editNameVal); if(e.key==="Escape") setEditingName(null); }}
                        style={{fontSize:14,fontWeight:600,padding:"3px 8px",borderRadius:6,
                                border:"1px solid var(--blue)",background:"var(--card)",color:"var(--gray-800)",
                                width:"100%",maxWidth:180}}
                      />
                      <button onClick={()=>renameBudget(b.cat,editNameVal)}
                        style={{background:"var(--blue)",border:"none",color:"white",cursor:"pointer",
                                fontSize:12,padding:"4px 8px",borderRadius:6,fontWeight:600,flexShrink:0,display:"flex",alignItems:"center"}}>
                        <Check size={13}/>
                      </button>
                      <button onClick={()=>setEditingName(null)}
                        style={{background:"var(--gray-100)",border:"none",color:"var(--gray-500)",cursor:"pointer",
                                fontSize:12,padding:"4px 8px",borderRadius:6,flexShrink:0,display:"flex",alignItems:"center"}}>
                        <X size={13}/>
                      </button>
                    </div>
                  ) : (
                    <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                      <p style={{fontSize:15,fontWeight:700,color:"var(--gray-800)"}}>{b.cat}</p>
                      <button onClick={()=>{ setEditingName(b.cat); setEditNameVal(b.cat); }}
                        style={{background:"none",border:"none",color:"var(--gray-400)",cursor:"pointer",
                                fontSize:12,padding:"2px 4px",borderRadius:4,lineHeight:1,display:"flex",alignItems:"center"}}
                        title={lang==="en"?"Rename":"Renombrar"}>
                        <Pencil size={13}/>
                      </button>
                    </div>
                  )}
                  <p style={{fontSize:13,color:"var(--gray-500)"}}>
                    {lang==="en"?"Spent":"Gastado"}: {fmt(b.spent)} · {lang==="en"?"Remaining":"Restante"}: <span style={{color:b.remaining>=0?"var(--green)":"var(--red)",fontWeight:600,display:"inline-flex",alignItems:"center",gap:3}}>{fmt(Math.abs(b.remaining))}{b.remaining<0 && <AlertTriangle size={12}/>}</span>
                  </p>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:8,flexShrink:0}}>
                  <span style={{fontSize:18,fontWeight:800,color:b.pct>=100?"var(--red)":b.pct>=80?"var(--amber)":"var(--gray-700)"}}>{b.pct}%</span>
                  <button type="button" onClick={()=>removeBudget(b.cat)}
                    style={{background:"var(--red-l)",border:"none",color:"var(--red)",cursor:"pointer",
                            fontSize:13,padding:"4px 8px",borderRadius:6,fontWeight:600,display:"flex",alignItems:"center"}}><X size={14}/></button>
                </div>
              </div>

              {/* Selector de color */}
              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:8,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:"var(--gray-400)",display:"flex",alignItems:"center"}}><Palette size={12}/></span>
                {COLOR_PALETTE.map(cp => {
                  const isSelected = (budgetColors[b.cat] || "theme") === cp.id;
                  const displayColor = cp.color || "var(--blue)";
                  return (
                    <button key={cp.id} type="button" onClick={()=>updateColor(b.cat, cp.id)}
                      title={cp.id}
                      style={{
                        width:16, height:16, borderRadius:"50%", border:"none", cursor:"pointer",
                        background: displayColor,
                        outline: isSelected ? "2px solid var(--gray-600)" : "none",
                        outlineOffset: 2,
                        transform: isSelected ? "scale(1.25)" : "scale(1)",
                        transition:"transform .1s",
                        flexShrink:0,
                      }}/>
                  );
                })}
              </div>

              {/* Barra con color personalizado */}
              <div className="limit-bar" style={{height:12,borderRadius:6}}>
                <div style={{
                  height:"100%", borderRadius:6,
                  width:`${b.pct}%`,
                  background: b.pct>=100 ? "var(--red)" : b.pct>=80 ? "var(--amber)" : getBarColor(b.cat),
                  transition:"width .3s"
                }}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
                <span style={{fontSize:11,color:"var(--gray-400)"}}>0</span>
                <span style={{fontSize:11,color:"var(--gray-400)",fontWeight:600}}>{fmt(b.limit)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {budgetItems.length === 0 && (
        <div className="empty" style={{marginBottom:24}}>
          <div className="empty-icon"><Target size={28}/></div>
          <h3>{lang==="en"?"No budgets yet":"Sin presupuestos todavía"}</h3>
          <p>{lang==="en"?"Add a budget below to start tracking your spending":"Añade un presupuesto abajo para empezar a controlar tus gastos"}</p>
        </div>
      )}

      {/* Añadir nuevo presupuesto */}
      <div className="card">
        <h3 style={{fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
          <Plus size={16}/> {lang==="en"?"Add budget":"Añadir presupuesto"}
        </h3>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>
          <div style={{flex:2,minWidth:150}}>
            <label style={{fontSize:12,color:"var(--gray-500)",display:"block",marginBottom:4}}>
              {lang==="en"?"Category":"Categoría"}
            </label>
            <select value={newCat} onChange={e=>setNewCat(e.target.value)}
              style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--border)",fontSize:14,fontFamily:"var(--font)",background:"var(--card)"}}>
              <option value="">{lang==="en"?"Select category":"Selecciona categoría"}</option>
              {expenseCats.filter(cat => !budgets[cat]).map(cat =>
                <option key={cat} value={cat}>{cat}</option>
              )}
            </select>
          </div>
          <div style={{flex:1,minWidth:100}}>
            <label style={{fontSize:12,color:"var(--gray-500)",display:"block",marginBottom:4}}>
              {lang==="en"?"Limit":"Límite"} ({sym})
            </label>
            <input type="number" placeholder="0.00" value={newAmt}
              onChange={e=>setNewAmt(e.target.value)}
              style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid var(--border)",fontSize:14,fontFamily:"var(--font)",background:"var(--card)"}}/>
          </div>
          <button onClick={addBudget} className="btn btn-primary">
            {lang==="en"?"Add":"Añadir"}
          </button>
        </div>
      </div>
    </div>
  );
}
